import { Car, Sparkles, Store, UserRound } from 'lucide-react';
import type { PaymentTokenMetadata, UpsellPaymentSummary } from '../api/paymentApi';
import {
  PAYMENT_CARD_CLASS,
  SUMMARY_SECTION_ID,
  getVehicleLabel,
  type PaymentAmounts,
  type PaymentMethodKind,
} from '../utils/paymentDisplay';
import { AmountBreakdown } from './PaymentAmountSummary';
import { AssistanceDetails, CoverageFacts, CoveredParts } from './CoverageDetails';

interface PaymentSummaryCardProps {
  metadata: PaymentTokenMetadata | null;
  /** Récapitulatif complet du garant (contrats upsell) ; null = montants seuls. */
  upsell: UpsellPaymentSummary | null;
  amounts: PaymentAmounts | null;
  methodKind: PaymentMethodKind;
  className?: string;
}

/**
 * Carte « Récapitulatif » : offre, véhicule, bénéficiaire, couverture (upsell),
 * puis montants à régler.
 */
export function PaymentSummaryCard({ metadata, upsell, amounts, methodKind, className = '' }: PaymentSummaryCardProps) {
  const summary = upsell?.summary ?? null;
  const vehicle = getVehicleLabel(metadata);
  const headline = summary?.warrantyName || 'Votre contrat';
  const sellerName = !upsell ? metadata?.companyName : null;

  return (
    <section
      id={SUMMARY_SECTION_ID}
      aria-labelledby="payment-summary-title"
      className={`${PAYMENT_CARD_CLASS} tw-scroll-mt-6 tw-overflow-hidden ${className}`}
    >
      <div className="tw-px-5 tw-py-5 sm:tw-px-6 sm:tw-pt-6">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <h2
            id="payment-summary-title"
            className="tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-600"
          >
            Récapitulatif
          </h2>
          {summary?.isLastChance && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[color:var(--pay-accent)] tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-[color:var(--pay-accent-text)]">
              <Sparkles className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
              Offre exclusive
            </span>
          )}
        </div>

        <p className="tw-mb-0 tw-mt-2 tw-text-xl tw-font-bold tw-leading-tight tw-text-gray-900 sm:tw-text-2xl">
          {headline}
        </p>
        {summary?.contractLabel && (
          <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-gray-600">Contrat n° {summary.contractLabel}</p>
        )}

        {(vehicle || summary?.beneficiaryName || sellerName) && (
          <ul className="tw-mb-0 tw-mt-4 tw-flex tw-list-none tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-pl-0 tw-text-sm tw-text-gray-700">
            {vehicle && (
              <li className="tw-inline-flex tw-items-center tw-gap-2">
                <Car className="tw-h-4 tw-w-4 tw-text-gray-400" aria-hidden="true" />
                <span>
                  <span className="tw-sr-only">Véhicule : </span>
                  {vehicle}
                </span>
              </li>
            )}
            {summary?.beneficiaryName && (
              <li className="tw-inline-flex tw-items-center tw-gap-2">
                <UserRound className="tw-h-4 tw-w-4 tw-text-gray-400" aria-hidden="true" />
                <span>
                  <span className="tw-sr-only">Bénéficiaire : </span>
                  {summary.beneficiaryName}
                </span>
              </li>
            )}
            {sellerName && (
              <li className="tw-inline-flex tw-items-center tw-gap-2">
                <Store className="tw-h-4 tw-w-4 tw-text-gray-400" aria-hidden="true" />
                <span>
                  <span className="tw-sr-only">Professionnel : </span>
                  {sellerName}
                </span>
              </li>
            )}
          </ul>
        )}

        {!summary && (
          <p className="tw-mb-0 tw-mt-4 tw-text-sm tw-text-gray-600">
            Vérifiez le montant ci-dessous puis renseignez vos coordonnées bancaires pour finaliser votre contrat.
          </p>
        )}
      </div>

      {summary && (
        <>
          <CoverageFacts summary={summary} />
          <CoveredParts summary={summary} />
          <AssistanceDetails assistance={summary.assistance} />
        </>
      )}

      {amounts && (
        <div className="tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-px-5 tw-py-5 sm:tw-px-6">
          <h3 className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-600">
            Votre règlement
          </h3>
          <AmountBreakdown amounts={amounts} methodKind={methodKind} />
        </div>
      )}
    </section>
  );
}
